import * as React from 'react'
import type { Block, SelectBlock } from '@basehub/data/replicache/index'

import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import {
  resolveSelectBlockValueFromAcceptedValues,
  sortSelectBlockAcceptedValues,
} from '@basehub/data/replicache/schema/block/select-utils'

import * as PropertiesPanel from '../../properties-panel'
import { useEditorMode } from '../../editor-mode-provider'
import { useRepoUndoManager } from '~/replicache/frontend/undo-redo/manager'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { getBlockFocusOp } from '~/replicache/frontend/undo-redo/util'
import { UpdateBlockParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/crud'
import { SelectOptionOpParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/select'
import { useTabsStore } from '../../tabs/store'

export const SelectBlockPropertiesPanel = ({
  block,
  parent,
  path,
}: {
  block: SelectBlock
  parent: Block
  path: string
}) => {
  const { data } = useRepoReplicache()
  const replicache = data?.replicache
  const editorMode = useEditorMode()
  const undoManager = useRepoUndoManager()
  const activeTabKey = useTabsStore((s) => s.activeTab?.key)
  const sourceBlockId = idToSourceBlockId(block)
  const getInputFocusOp = React.useCallback(
    (id: string) => {
      return getBlockFocusOp({
        block: {
          id: block.id,
          sourceBlockId: block.sourceBlockId,
        },
        preferredTabKey: activeTabKey,
        focusType: 'block:properties-panel',
        elementIdOverride: id,
      })
    },
    [block.id, block.sourceBlockId, activeTabKey]
  )

  return (
    <PropertiesPanel.PropertiesContainer>
      <PropertiesPanel.Group title="Constraints" spacing="tight">
        <PropertiesPanel.BooleanOption
          name="Is required"
          value={block.constraints.isRequired}
          id={`el-${sourceBlockId}-pp-is-required`}
          mutate={async (v) => {
            const params: UpdateBlockParams = {
              id: block.id,
              instanceChildTarget: block.instanceChildTarget,
              data: {
                constraints: { isRequired: v },
              },
              path,
              strategy: 'merge-deep',
              isoNow: new Date().toISOString(),
            }
            const result = await replicache?.mutate.updateBlock(params)
            if (result?.undo) {
              undoManager.current?.push({
                redo: [
                  getInputFocusOp(`el-${sourceBlockId}-pp-is-required`),
                  { op: 'updateBlock', args: params },
                ],
                undo: [
                  getInputFocusOp(`el-${sourceBlockId}-pp-is-required`),
                  {
                    op: 'blockBatchMutations',
                    args: { mutations: result.undo },
                  },
                ],
              })
            }
          }}
        />
        <PropertiesPanel.BooleanOption
          name="Allow multiple values"
          id={`el-${sourceBlockId}-pp-multiple-values`}
          value={block.constraints.multiple}
          mutate={async (v) => {
            const params: UpdateBlockParams = {
              id: block.id,
              instanceChildTarget: block.instanceChildTarget,
              data: { constraints: { multiple: v } },
              path,
              strategy: 'merge-deep',
              isoNow: new Date().toISOString(),
            }
            const result = await replicache?.mutate.updateBlock(params)
            if (result?.undo) {
              undoManager.current?.push({
                redo: [
                  getInputFocusOp(`el-${sourceBlockId}-pp-multiple-values`),
                  { op: 'updateBlock', args: params },
                ],
                undo: [
                  getInputFocusOp(`el-${sourceBlockId}-pp-multiple-values`),
                  {
                    op: 'blockBatchMutations',
                    args: { mutations: result.undo },
                  },
                ],
              })
            }
          }}
        />
        <PropertiesPanel.List
          id={`el-${sourceBlockId}-pp-allowed-values`}
          options={sortSelectBlockAcceptedValues(
            block.constraints.acceptedValues
          ).map((possibleValue) => ({
            name: possibleValue,
            value: possibleValue,
            selected: block.value.includes(possibleValue),
          }))}
          title="Allowed values"
          editable={editorMode === 'write:all'}
          mutate={async (newOptions) => {
            const newAcceptedValues = newOptions.map((v) => v.value)
            const newValue = resolveSelectBlockValueFromAcceptedValues(
              block,
              newAcceptedValues
            )

            const params: UpdateBlockParams = {
              id: block.id,
              instanceChildTarget: block.instanceChildTarget,
              data: {
                value: Array.from(new Set([...newValue])),
                constraints: {
                  acceptedValues: Array.from(new Set([...newAcceptedValues])),
                },
              },
              path,
              strategy: 'merge-deep',
              isoNow: new Date().toISOString(),
            }
            const result = await replicache?.mutate.updateBlock(params)
            if (result?.undo) {
              undoManager.current?.push({
                redo: [
                  getInputFocusOp(`el-${sourceBlockId}-pp-allowed-values`),
                  { op: 'updateBlock', args: params },
                ],
                undo: [
                  getInputFocusOp(`el-${sourceBlockId}-pp-allowed-values`),
                  {
                    op: 'blockBatchMutations',
                    args: { mutations: result.undo },
                  },
                ],
              })
            }
          }}
          mutateRenameOption={async (data) => {
            const params: SelectOptionOpParams = {
              id: block.id,
              data: { op: 'rename', ...data },
              path,
              isoNow: new Date().toISOString(),
            }
            const result = await replicache?.mutate.selectOptionOp(params)
            if (result?.undo) {
              undoManager.current?.push({
                redo: [
                  getInputFocusOp(`el-${sourceBlockId}-pp-allowed-values`),
                  { op: 'selectOptionOp', args: params },
                ],
                undo: [
                  getInputFocusOp(`el-${sourceBlockId}-pp-allowed-values`),
                  {
                    op: 'blockBatchMutations',
                    args: { mutations: result.undo },
                  },
                ],
              })
            }
          }}
          mutateDeleteOption={async (data) => {
            const params: SelectOptionOpParams = {
              id: block.id,
              data: { op: 'delete', ...data },
              path,
              isoNow: new Date().toISOString(),
            }
            const result = await replicache?.mutate.selectOptionOp(params)
            if (result?.undo) {
              undoManager.current?.push({
                redo: [
                  getInputFocusOp(`el-${sourceBlockId}-pp-allowed-values`),
                  { op: 'selectOptionOp', args: params },
                ],
                undo: [
                  getInputFocusOp(`el-${sourceBlockId}-pp-allowed-values`),
                  {
                    op: 'blockBatchMutations',
                    args: { mutations: result.undo },
                  },
                ],
              })
            }
          }}
        />
      </PropertiesPanel.Group>

      <PropertiesPanel.CommonSearchConfigGroup
        block={block}
        parent={parent}
        path={path}
      />
    </PropertiesPanel.PropertiesContainer>
  )
}
